import { html } from 'lit';
import { ViewBase } from '../view-base.js';
import { adminPubsubViewStyles } from './admin-pubsub-view-styles.js';

import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/format-date/format-date.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/menu-item/menu-item.js';
import '@shoelace-style/shoelace/dist/components/option/option.js';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/switch/switch.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';

import { Q1SpinnerPanel } from '../../components/q1-spinner-panel/q1-spinner-panel.js';
import { FormHostMixin } from '../../modules/form-host-mixin.js';

import { q1AdminPubsubFunctions } from '../../modules/q1-admin-functions.js';

export class AdminPubsubView extends FormHostMixin(ViewBase) {
  static styles = [
    ...super.styles,
    adminPubsubViewStyles,
  ];

  static properties = {
    ...super.properties,

    loading: { type: Object },

    topics: { type: Array },
    schemas: { type: Object },

    selectedTopic: { type: String },
    selectedSchemaFields: { type: Array },
    messageData: { type: Object },
  };

  constructor() {
    super();

    this.loading = true;

    this.topics = [];
    this.schemas = {};

    this.selectedTopic = null;
    this.selectedSchemaFields = [];
    this.messageData = {};
  }

  connectedCallback() {
    super.connectedCallback();
    this.setViewMeta();

    this.initView();
  }

  async initView() {
    this.loading = true;
    
    const { getSettings } = q1AdminPubsubFunctions;
    
    try {
      const response = await getSettings();
      const { topics, schemas } = response.data || {};

      this.topics = topics;
      this.schemas = schemas;
    } catch (error) {
      console.error('Error fetching settings:', error);
      this.showToast(error.message, 'Error Loading Settings', 'danger');
    } finally {
      this.loading = false;
    }
  }

  handleTopicChange(event) {
    this.selectedTopic = event.target.value;
    const schema = this.schemas[this.selectedTopic] || {};

    this.selectedSchemaFields = Object.entries(schema.properties || {}).map(([key, field]) => ({
      key,
      type: field.type,
      label: field.label,
      required: schema.required?.includes(key),
      enumOptions: field.enum,
    }));
  }

  async handleSubmit(event) {
    event.preventDefault();

    const messageForm = this.renderRoot.getElementById('message-form');
    if (!messageForm.reportValidity()) {
      this.showToast('Please fill out all required fields', 'Form Incomplete', 'danger');
      return;
    }

    this.loading = true;

    const { publishMessage } = q1AdminPubsubFunctions;

    console.log('Selected Topic:', this.selectedTopic);
    console.log('Message Data:', this.messageData);

    try {
      const response = await publishMessage({
        topic: this.selectedTopic,
        json: this.messageData,
      });

      console.log('Message published:', response);
      this.showToast(response.data?.message, 'Message Published Successfully', 'success');

    } catch (error) {
      console.error('Error publishing message:', error);
      this.showToast(error.message, 'Error Publishing Message', 'danger');
    } finally {
      this.loading = false;
    }
  }

  get contentTemplate() {
    return html`
      <h1>Admin Panel: PubSub</h1>

      <p class="subtitle">Use this panel to publish messages to PubSub topics.</p>

      <sl-divider></sl-divider>

      ${!this.topics.length ? null : html`
        <sl-select label="Select a Topic" @sl-change=${this.handleTopicChange}>
          ${this.topics.map(topic => html`<sl-option value=${topic}>${topic}</sl-option>`)}
        </sl-select>

        ${!this.selectedSchemaFields.length ? null : html`
          <form class="q1" id="message-form" @submit=${this.handleSubmit}>
            ${this.selectedSchemaFields.map(fieldItem => this.inputTemplate(fieldItem))}

            <sl-button
              variant="primary"
              type="submit"
              size="large"
              ?disabled=${!this.selectedTopic || this.loading}
            >Publish Message</sl-button>
          </form>
        `}
      `}

      <q1-spinner-panel ?open=${this.loading} label="Loading..."></q1-spinner-panel>
    `;
  }

  inputTemplate({ key, type, enumOptions, label, required } = {}) {
    switch (type) {
      case 'array':
        return html`
          <sl-input
            id=${key}
            name=${`messageData.${key}`}
            label=${label}
            @sl-input=${this.handleArray}
            ?required=${required}
          >
            <span slot="help-text">Enter comma-separated values</span>
          </sl-input>
        `;
      case 'object':
        return html`
          <sl-textarea
            id=${key}
            name=${`messageData.${key}`}
            label=${label}
            rows="10"
            @sl-change=${this.handleObject}
            ?required=${required}
            help-text="Enter a JSON object"
          ></sl-textarea>
        `;
      case 'boolean':
        return html`
          <sl-switch
            id=${key}
            name=${`messageData.${key}`}
            @sl-change=${this.handleChecked}
            ?required=${required}
          >${label}</sl-switch>
        `;
      case 'string':
        if (enumOptions?.length) return html`
          <sl-select 
            id=${key}
            name=${`messageData.${key}`}
            label=${label}
            @sl-change=${this.handleChange}
            ?required=${required}
          >
            ${enumOptions.map((option) => html`
              <sl-option value=${option}>${option}</sl-option>
            `)}
          </sl-select>
        `;

        // else continue to the default return
      
      default:
        return html`
          <sl-input
            id=${key}
            name=${`messageData.${key}`}
            label=${label}
            @sl-input=${this.handleInput}
            ?required=${required}
          ></sl-input>
        `;
    }
  }

}

customElements.define('admin-pubsub-view', AdminPubsubView);
